import './../../../style/hexagons.css';

import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from 'react-router-dom';

import hexagonFront from "../../../img/solutions/hexagonFront.svg";
import hexagonFrontDW from "../../../img/solutions/hexagonFrontDW.svg";
import hexagonBack from "../../../img/solutions/hexagonBack.svg";

import ArticleWrapper from './ArticleWrapper';

const Hexagons = ({ hexagonContent, pageTitle, pageSubTitle }) => {

    // Define generetional variables
    const hexWidth = 250;
    const hexGapHorizontal = 0;
    const hexGapVertical = -1 * 28;
    const elementWidth = .65;

    // Navigation variables
    const navigate = useNavigate();
    const location = useLocation();
    const articles = []

    // Title varaiables
    const [title, setTitle] = useState(pageTitle);
    const [subTitle, setSubTitle] = useState(pageSubTitle);

    // Animation variables
    const delay = 200;
    const scaleAnimation = `1.15`;
    const scaleAnimationDelay = 300;
    const opacityAnimationDelay = 100;
    const chosenHexAnimationDelay = 750;
    const transitionSpeed = `.5`;

    const [hexBackOpacity, setHexBackOpacity] = useState(1);
    const [chosenHexIndex, setChosenHexIndex] = useState(null);
    const chosenHex = useRef(null);
    const allHexRef = useRef([]);
    const [hexDelay, setHexDelay] = useState([]);

    // Hexs style
    const hexagonStyle = () => {
        return ({
            width: `${hexWidth}px`,
            height: `${hexWidth}px`,
            backgroundColor: 'transparent',
            perspective: '1000px',
            margin: `${hexGapVertical}px ${hexGapHorizontal}px`,
        });
    };

    const hexagonHalfStyle = () => {
        return ({
            width: `${hexWidth}px`,
            height: `${hexWidth}px`,
            backgroundColor: 'transparent',
            perspective: '1000px',
            margin: `${hexGapVertical}px 0`,
        });
    };

    const hexagonInnerStyle = (calculatedIndex) => {

        // If none hex was chosen
        if (chosenHexIndex == null) {
            return ({});
        }

        // If different hex was chosen
        if (chosenHexIndex != calculatedIndex) {
            return ({
                transform: `rotateX(180deg)`,
                scale: scaleAnimation,
                opacity: `0`,
                transition: `
                    transform ${transitionSpeed}s ease ${hexDelay[calculatedIndex]}ms,
                    scale ${transitionSpeed}s ease ${(hexDelay[calculatedIndex] + scaleAnimationDelay)}ms,
                    opacity ${transitionSpeed}s ease ${(hexDelay[calculatedIndex] + scaleAnimationDelay + opacityAnimationDelay)}ms
                `
            });
        }

        // If this hex was chosen
        if (chosenHexIndex == calculatedIndex) {

            // Calculate transition delay
            let transitionDelay = Math.max(...hexDelay) + chosenHexAnimationDelay;

            return ({
                transform: `roteteX(180deg)`,
                scale: `${(scaleAnimation * 1.5)}`,
                opacity: `0`,
                transition: `
                    scale ${transitionSpeed}s ease ${transitionDelay}ms,
                    opacity ${transitionSpeed}s ease ${transitionDelay}ms
                `
            });
        }
    };

    const hexagonBackStyle = (calculatedIndex) => {

        // If none hex was chosen
        if (chosenHexIndex == null)
            return ({});

        // If different hex was chosen
        if (chosenHexIndex != calculatedIndex)
            return ({ opacity: hexBackOpacity });

        // If this hex was chosen
        if (chosenHexIndex == calculatedIndex)
            return ({ transform: `rotateX(0deg)` });
    };

    const hexagonImgStyle = (calculatedIndex) => {
        return (
            chosenHexIndex != calculatedIndex ? {
                opacity: hexBackOpacity,
                transition: `all ${transitionSpeed}s ease`
            } : {}
        );
    };

    const articleStyle = () => {
        return (
            hexBackOpacity == 0 ? {
                opacity: 0,
                animationName: 'fadeIn',
                animationDuration: '1s',
                animationFillMode: 'forwards',
                animationDelay: `${Math.max(...hexDelay) + 2 * chosenHexAnimationDelay}ms`
            } : {}
        );
    }



    // Calculate max row size
    const maxElementsPerRow = Math.floor((window.innerWidth * elementWidth) / (hexWidth + (2 * hexGapHorizontal)));

    // Define row sizes
    let index = 0;
    let elementsPerRow = [];
    let rows = 0;

    while (index < hexagonContent.length) {
        index = index + maxElementsPerRow - 1 + rows % 2;
        elementsPerRow.push(maxElementsPerRow + 1 + rows % 2); // all hexs + 2 empty ones
        rows++;
    }

    // Choose popup and show it
    const choosePopup = (row, index, calculatedIndex, url, title, subTitle) => {

        // Reset hexs
        if (row == -1 && index == -1) {
            chosenHex.current = null;
            setChosenHexIndex(null);
            setHexBackOpacity(1);
            navigate(`${location.pathname}`);
            setTitle(pageTitle);
            setSubTitle(pageSubTitle);

            return;
        }

        // Set page title
        setTitle(title);
        setSubTitle(subTitle);

        // Set chosen hex ref
        chosenHex.current = allHexRef.current[calculatedIndex]; //! to fix

        // Calculate delay for all hexagons
        for (let i = 0; i < rows; i++) {
            for (let j = 0; j < elementsPerRow[i]; j++) {
                hexDelay[calculateIndex(i, j)] = calculateDelay(row, index, i, j);
            }
        }

        // Rotate all hexagons
        setChosenHexIndex(calculatedIndex);
        setHexBackOpacity(0);

        // Change URL
        navigate(`${location.pathname}?article=${url}`);
    };

    const calculateDelay = (rowA, indexA, rowB, indexB) => {

        let rowDiff = Math.abs(rowA - rowB);
        let indexDiff = Math.abs(indexA - indexB);

        if (rowDiff == 1 && indexA + (rowA % 2 == 0 ? 1 : -1) == indexB) {
            rowDiff = 0;
            indexDiff = 1;
        }

        return (rowDiff + indexDiff) * delay;
    }

    // Calculate element index
    const calculateIndex = (row, index) => {
        let calculatedIndex = 0;

        for (let i = 0; i < row; i++)
            calculatedIndex += elementsPerRow[i];

        return calculatedIndex + index;
    };


    // Define static elements
    const fullEmptyHex = (calculatedIndex) => {
        return (
            <div className="solutions-hexagon" ref={el => allHexRef.current[calculatedIndex] = el} style={hexagonStyle()}>
                <div className={chosenHexIndex == null ? "solutions-hexagon-inner solutions-hexagon-noanim" : "solutions-hexagon-inner"}
                    style={hexagonInnerStyle(calculatedIndex)}>

                    <div className="solutions-hexagon-front">
                        <div className="solutions-hexagon-front-inner">
                            <img src={hexagonFront} alt="Front" />
                        </div>
                    </div>

                    <div className="solutions-hexagon-back"
                        style={hexagonBackStyle(calculatedIndex)}>
                        <div className="solutions-hexagon-back-inner">
                            <img src={hexagonBack} alt="Back" />
                        </div>

                    </div>
                </div>
            </div>
        );
    }

    const clippedEmptyHexLeft = (calculatedIndex) => {
        return (
            <div className="solutions-hexagon solutions-hexagon-clipped-left" style={hexagonHalfStyle()}>
                <div className={chosenHexIndex == null ? "solutions-hexagon-inner solutions-hexagon-noanim" : "solutions-hexagon-inner"}
                    style={hexagonInnerStyle(calculatedIndex)}>

                    <div className="solutions-hexagon-front">
                        <div className="solutions-hexagon-front-inner">
                            <img src={hexagonFront} alt="Front" />
                        </div>
                    </div>

                    <div className="solutions-hexagon-back" style={hexagonBackStyle(calculatedIndex)}>
                        <div className="solutions-hexagon-back-inner">
                            <img src={hexagonBack} alt="Back" />
                        </div>

                    </div>
                </div>
            </div>
        );
    }

    const clippedEmptyHexRight = (calculatedIndex) => {
        return (
            <div className="solutions-hexagon-clipped-right" style={hexagonHalfStyle()}>
                <div className={chosenHexIndex == null ? "solutions-hexagon-inner solutions-hexagon-noanim" : "solutions-hexagon-inner"}
                    style={hexagonInnerStyle(calculatedIndex)}>

                    <div className="solutions-hexagon-front">
                        <div className="solutions-hexagon-front-inner">
                            <img src={hexagonFront} alt="Front" />
                        </div>
                    </div>

                    <div className="solutions-hexagon-back"
                        style={hexagonBackStyle(calculatedIndex)}>
                        <div className="solutions-hexagon-back-inner">
                            <img src={hexagonBack} alt="Back" />
                        </div>
                    </div>

                </div>
            </div>
        );
    }

    // Populate rows
    let rowsTab = [];
    let srcIndex = 0;
    for (let row = 0; row < rows; row++) {

        // Individual hexagons
        let items = [];

        // Populate row
        for (let elementIndex = 0; elementIndex < elementsPerRow[row]; elementIndex++) {

            // Breake loop if all hexagons are placed
            if ((row + elementIndex) >= hexagonContent.length)
                break;

            // Calculate current element index
            let calculatedIndex = calculateIndex(row, elementIndex);

            // Add empty hex on row start
            if (elementIndex == 0) {
                if (row % 2 == 0)
                    items.push(fullEmptyHex(calculatedIndex));
                else
                    items.push(clippedEmptyHexLeft(calculatedIndex));
            }

            // Add empty hex on row end
            else if (elementIndex == (elementsPerRow[row] - 1)) {
                if (row % 2 == 0)
                    items.push(fullEmptyHex(calculatedIndex));
                else
                    items.push(clippedEmptyHexRight(calculatedIndex));
            }

            // Add normal hex if its not on row start or end
            else {

                // Create element text
                const element = hexagonContent[srcIndex++];
                const elementText = [];

                // Create element URL
                let elementURL = element.text.join('-');
                articles.push({ key: elementURL, value: element.content });

                // Add break line for element text ending
                element.text.forEach((line) => { elementText.push(<>{line} <br /></>) });

                items.push(
                    <div key={calculatedIndex}>
                        <div className={`${chosenHexIndex == calculatedIndex ? "" : "solutions-hexagon-hoverable"}`}
                            onClick={() => choosePopup(
                                row,
                                elementIndex,
                                calculatedIndex,
                                elementURL,
                                element.title,
                                element.subTitle
                            )}
                            ref={el => allHexRef.current[calculatedIndex] = el}
                            style={hexagonStyle()}>

                            <div className="solutions-hexagon-inner"
                                style={hexagonInnerStyle(calculatedIndex)}>

                                {/* Front */}
                                <div className="solutions-hexagon-front">
                                    <div className="solutions-hexagon-front-inner">
                                        <img src={hexagonFront} alt="Front" />
                                        <div className="solutions-hexagon-icon">

                                            {/* //* Uncomment for debug */}
                                            {/* <p>{calculatedIndex}</p> */}
                                            {/* <div>{hexDelay[calculatedIndex]}</div> */}

                                            <img src={element.img}
                                                alt="icon"
                                                style={hexagonImgStyle(calculatedIndex)} />
                                        </div>
                                    </div>
                                </div>

                                {/* Back */}
                                <div className="solutions-hexagon-back"
                                    style={hexagonBackStyle(calculatedIndex)}>
                                    <div className="solutions-hexagon-back-inner">
                                        <img src={hexagonBack} alt="Back" />
                                    </div>
                                    <h1>
                                        {elementText}
                                    </h1>
                                </div>

                            </div>
                        </div>
                    </div>
                );
            }
        }

        // Add row to table
        rowsTab.push(<div className='hex-row' key={row}>{items}</div>);
    }

    // Offset and Move hexagons closer
    //? TODO uncomment for production
    //? delete?
    // useEffect(() => {
    //     const hexagonRows = document.querySelectorAll('.hex-row');

    //     hexagonRows.forEach((hexagonRow, index) => {
    //             hexagonRow.style.transform = `translate(0, ${(index - 1) * -25}%)`;
    //     });
    // }, [])


    return (
        <div>
            <div id="hexagon-container2">

                {/* Title */}
                <div className='solutions-title-section'>

                    <div className='solutions-title'>
                        <h1>{title}</h1>
                    </div>

                    <div className='solutions-title-bar' />

                    <div className='solutions-sub-title'>
                        <h2>{subTitle}</h2>
                    </div>

                </div>

                {/* Hexagons */}
                <div className=''>
                    {rowsTab}
                </div>

                {/* Article */}
                <div className='article-wrapper-container' style={articleStyle()}>
                    <div className='article-wrapper-inner-container' style={{ width: `${100 * elementWidth}%` }}>
                        <ArticleWrapper articles={articles} />
                        <div className='go-back-arrow'
                            onClick={() => choosePopup(-1, -1)}
                            style={{ display: hexBackOpacity === 1 ? 'none' : 'flex' }}>
                            {"←"}
                        </div>
                    </div>
                </div>

            </div>
        </div>
    )
}

export default Hexagons
